import { useState } from 'react';
import { Wallet, ShieldCheck, Lock } from 'lucide-react';
import { useWallet } from '../../contexts/WalletContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../../components/ui/input-otp';
import { toast } from 'sonner';

export function WalletSetupModal() {
  const { showSetupModal, setShowSetupModal, setupWallet, pendingAction, setPendingAction } = useWallet();
  const [step, setStep] = useState<'intro' | 'create' | 'confirm'>('intro');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setShowSetupModal(false);
    setStep('intro');
    setPin('');
    setConfirmPin('');
    setError('');
    setPendingAction(null);
  };

  const handleCreatePin = () => {
    if (pin.length !== 8) {
      setError('PIN deve ter 8 dígitos');
      return;
    }
    setError('');
    setStep('confirm');
  };

  const handleConfirmPin = async () => {
    if (confirmPin !== pin) {
      setError('Os PINs não coincidem');
      setConfirmPin('');
      return;
    }

    setIsLoading(true);
    const success = await setupWallet(pin);
    setIsLoading(false);

    if (success) {
      toast.success('Wallet configurada com sucesso!');
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
      handleClose();
    } else {
      setError('Erro ao configurar wallet');
    }
  };

  return (
    <Dialog open={showSetupModal} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/20">
              <Wallet className="h-8 w-8 text-accent" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {step === 'intro' && 'Configurar Wallet'}
            {step === 'create' && 'Criar PIN de Segurança'}
            {step === 'confirm' && 'Confirmar PIN'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {step === 'intro' && 'Configure a sua wallet para poder assinar documentos eCMR de forma segura.'}
            {step === 'create' && 'Escolha um PIN de 8 dígitos para proteger a sua wallet.'}
            {step === 'confirm' && 'Confirme o PIN para finalizar a configuração.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'intro' && (
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <ShieldCheck className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Segurança</p>
                  <p className="text-xs text-muted-foreground">
                    A sua wallet está protegida por um PIN de 8 dígitos.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Lock className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Assinaturas</p>
                  <p className="text-xs text-muted-foreground">
                    Use a wallet para assinar eCMRs de forma segura e verificável.
                  </p>
                </div>
              </div>
            </div>
            <Button onClick={() => setStep('create')} className="w-full">
              Começar Configuração
            </Button>
          </div>
        )}

        {step === 'create' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <InputOTP
                value={pin}
                onChange={setPin}
                maxLength={8}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                  <InputOTPSlot index={6} />
                  <InputOTPSlot index={7} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('intro')} className="flex-1">
                Voltar
              </Button>
              <Button onClick={handleCreatePin} className="flex-1" disabled={pin.length !== 8}>
                Continuar
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <InputOTP
                value={confirmPin}
                onChange={setConfirmPin}
                maxLength={8}
                autoFocus
              >
                <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                    <InputOTPSlot index={6} />
                    <InputOTPSlot index={7} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStep('create'); setConfirmPin(''); setError(''); }} className="flex-1">
                Voltar
              </Button>
              <Button
                onClick={handleConfirmPin}
                className="flex-1"
                disabled={confirmPin.length !== 8 || isLoading}
              >
                {isLoading ? 'A configurar...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
